import { useEffect, useRef } from 'react'
import { appStore, isApplicationLocked } from '@/store'
import type { ApplicationStatus } from '@leangap/types'
// no extra imports needed

/**
 * Keeps the application_status for a step in sync with form validity, but only updates
 * after the form is valid at least once and only if the value actually changes.
 * Prevents sidebar flicker on navigation.
 */
export function useStepStatusSync<K extends keyof ApplicationStatus>(
	stepKey: K,
	isValid: boolean,
	isDirty: boolean,
	isSubmitted: boolean
) {
	// const hasValidated = useRef(false)
	// Read transient flags once so they can be used in dependency array cleanly
	const { fileOperation, saving } = appStore.snapshot()

	const hasMounted = useRef(false)
	const prevValid = useRef<boolean | null>(null)

	useEffect(() => {
		const snap = appStore.snapshot()
		const user = snap.user

		// If the app is performing a file operation or saving, skip updating the
		// application_status. These transient operations can cause brief
		// inconsistent states which would otherwise flip the sidebar. Wait until
		// operations finish and re-run.
		if (fileOperation || saving) return

		// Do not patch status if application is locked (Submitted, Admitted, Not Accepted)
		if (isApplicationLocked(user?.application_process)) return

		// Read visited flag from localStorage (if available). We don't rely on
		// this solely to patch on mount, but it's useful to allow patching when
		// the user previously visited the form.
		let visited = false
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				visited = Boolean(localStorage.getItem(`formVisited:${String(stepKey)}`))
			}
		} catch {
			visited = false
		}

		// runtime snapshot for conditional logic

		// On the very first run (mount), don't perform writes — only observe state.
		if (!hasMounted.current) {
			hasMounted.current = true
			prevValid.current = isValid
			return
		}

		// Only patch when validity changed (to avoid redundant writes), and only
		// if the user has interacted (dirty) or explicitly submitted the form,
		// or if they had previously visited the form (visited).
		const validityChanged = prevValid.current !== isValid
		prevValid.current = isValid

		if (!validityChanged) return

		if (!isDirty && !isSubmitted && !visited) {
			return
		}

		let id: ReturnType<typeof setTimeout> | null = null
		if (
			user &&
			typeof user.application_status === 'object' &&
			user.application_status[stepKey] !== isValid
		) {
			// Debounce quick successive updates to avoid visible sidebar flicker
			id = setTimeout(() => {
				appStore.patch({
					application_status: {
						...user.application_status,
						[stepKey]: isValid,
					},
				})
			}, 40)
		}

		return () => {
			if (id) clearTimeout(id)
		}
	}, [isValid, stepKey, isDirty, isSubmitted, fileOperation, saving])
}
